import { useState } from 'react'
import { RadioGroup } from 'cwp-components/forms/RadioGroup'
import { useSearchFilters } from 'hooks/useSearchFilters'
import { Button } from 'cwp-components/forms/Buttons'
import { SelectSearch } from 'cwp-components/forms/SelectSearch'
import { useNavigate } from 'react-router-dom'

interface ProSearchProps {}

const ProSearch = ({}: ProSearchProps) => {
  const navigate = useNavigate()
  const [selectedAreaOfLaw, setSelectedAreaOfLaw] = useState([])
  const [selectedSubAreasOfLaw, setSelectedSubAreasOfLaw] = useState([])
  const [selectedLegalServices, setSelectedLegalServices] = useState([])
  const [selectedCity, setSelectedCity] = useState([])
  const [selectedRole, setSelectedRole] = useState('')
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null)

  const { lawAreas, lawSubAreas, cities, legalServices } = useSearchFilters()
  const handleSearch = () => {
    const params = new URLSearchParams()

    if (selectedCity.length) {
      params.set('city', selectedCity[0])
    }
    if (selectedRole.length) {
      params.set('lawyer_type', selectedRole.toUpperCase())
    }
    if (selectedAreaOfLaw.length) {
      params.set('area_id', selectedAreaOfLaw.join(','))
    }
    if (selectedSubAreasOfLaw.length) {
      params.set('subarea_id', selectedSubAreasOfLaw.join(','))
    }
    if (selectedLegalServices.length) {
      params.set('legal_service_id', selectedLegalServices.join(','))
    }

    navigate(`/find-a-lawyer/search?${params.toString()}`)
  }

  return (
    <div className='flex flex-col items-stretch gap-6 w-full'>
      <div className='flex flex-wrap md:flex-nowrap gap-6 w-full'>
        <div className='flex flex-col gap-6 w-full md:w-auto'>
          <SelectSearch
            key='AreaOfLaw'
            id='AreaOfLaw'
            options={lawAreas}
            placeholder='Area of Law'
            withSearch
            isOpen={activeDropdownId === 'AreaOfLaw'}
            onToggle={() =>
              setActiveDropdownId((prev) =>
                prev === 'AreaOfLaw' ? null : 'AreaOfLaw',
              )
            }
            onSelect={(areaIds: string[]) => {
              setSelectedAreaOfLaw(areaIds)
              const filteredSubareas = selectedSubAreasOfLaw.filter((subId) => {
                const sub = lawSubAreas.find((s) => s.value === subId)
                return sub && areaIds.includes(sub.area_id?.toString())
              })
              setSelectedSubAreasOfLaw(filteredSubareas)
            }}
            isLoading={lawAreas.length === 0}
            value={selectedAreaOfLaw}
            multiple
          />

          <SelectSearch
            key='SubAreaOfLaw'
            id='SubAreaOfLaw'
            options={lawSubAreas}
            placeholder='Subarea of Law'
            withSearch
            isOpen={activeDropdownId === 'SubAreaOfLaw'}
            onToggle={() =>
              setActiveDropdownId((prev) =>
                prev === 'SubAreaOfLaw' ? null : 'SubAreaOfLaw',
              )
            }
            onSelect={(selectedIds: string[]) => {
              setSelectedSubAreasOfLaw(selectedIds)

              const matchedAreas = lawSubAreas
                .filter((sub) => selectedIds.includes(sub.value))
                .map((sub) => sub.area_id?.toString())
              const newUniqueAreas = matchedAreas.filter(
                (id) => id && !selectedAreaOfLaw.includes(id),
              )
              setSelectedAreaOfLaw([...selectedAreaOfLaw, ...newUniqueAreas])
            }}
            isLoading={lawSubAreas.length === 0}
            cap={10}
            value={selectedSubAreasOfLaw}
            multiple
            limit={3}
          />
        </div>

        <div className='flex flex-col gap-6 w-full md:w-auto'>
          <SelectSearch
            key='LegalServices'
            id='LegalServices'
            options={legalServices}
            placeholder='Legal Services'
            isOpen={activeDropdownId === 'LegalServices'}
            onToggle={() =>
              setActiveDropdownId((prev) =>
                prev === 'LegalServices' ? null : 'LegalServices',
              )
            }
            onSelect={setSelectedLegalServices}
            isLoading={legalServices.length === 0}
            multiple
          />
          <SelectSearch
            key='City'
            id='City'
            options={cities}
            placeholder='City'
            isOpen={activeDropdownId === 'City'}
            onToggle={() =>
              setActiveDropdownId((prev) => (prev === 'City' ? null : 'City'))
            }
            onSelect={setSelectedCity}
            isLoading={cities.length === 0}
            withSearch
          />
        </div>

        <div className='flex flex-col gap-6 w-full md:w-auto'>
          <RadioGroup
            name='legal-role'
            options={['Barrister', 'Solicitor']}
            value={selectedRole}
            onChange={setSelectedRole}
          />
        </div>
      </div>

      <div className='flex justify-end w-full'>
        <Button
          onClick={handleSearch}
          className={
            !(
              selectedAreaOfLaw.length > 0 ||
              selectedSubAreasOfLaw.length > 0 ||
              selectedLegalServices.length > 0 ||
              selectedCity.length > 0 ||
              selectedRole.length > 0
            )
              ? 'bg-[#F4F4F4] text-gray-500 cursor-not-allowed'
              : 'text-white bg-[#020251]'
          }
        >
          Search
        </Button>
      </div>
    </div>
  )
}

export default ProSearch
